<template>
  <div id="icon-migration" class="p-4 md:p-8 text-[#cdd6ee]">
    <div class="max-w-6xl mx-auto">
      <div class="mb-6">
        <h1 class="text-2xl font-semibold mb-2">Icon Migration</h1>
        <p class="text-sm text-[#98a6c6]">
          Legacy `bly-*` vs new `ma-*` SVG icons (`icons-new`).
        </p>
      </div>

      <div class="stats-grid mb-6">
        <div class="stat-card">
          <div class="stat-label">Used Total</div>
          <div class="stat-value">{{ summary.usedTotal }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Added</div>
          <div class="stat-value text-green-400">{{ summary.added }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Missing</div>
          <div class="stat-value text-red-400">{{ summary.missing }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">Progress</div>
          <div class="stat-value">{{ summary.progressPct }}%</div>
        </div>
      </div>

      <div class="mb-4 flex items-center gap-3">
        <input
          v-model.trim="search"
          type="text"
          placeholder="Search icon name..."
          class="search-input"
        >
        <div class="text-xs text-[#98a6c6]">
          Manifest: <code>src/assets/icons-new/migration-manifest.json</code>
        </div>
      </div>

      <div class="table-wrap">
        <table class="migration-table">
          <thead>
            <tr>
              <th>Legacy</th>
              <th>New</th>
              <th>Status</th>
              <th>Usage</th>
              <th>Sample Refs</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredIcons" :key="item.legacyName">
              <td>
                <div class="icon-cell">
                  <i :class="['bi', item.legacyName]" class="preview-icon"></i>
                  <span class="name">{{ item.legacyName }}</span>
                </div>
              </td>
              <td>
                <div class="icon-cell">
                  <component
                    :is="item.newComponent"
                    v-if="item.newSvgExists"
                    class="preview-icon"
                  />
                  <span v-else class="preview-icon">-</span>
                  <span class="name">{{ item.newComponent }}</span>
                </div>
              </td>
              <td>
                <span class="status" :class="item.newSvgExists ? 'status-added' : 'status-missing'">
                  {{ item.newSvgExists ? 'added' : 'missing' }}
                </span>
              </td>
              <td>{{ item.usageCount }}</td>
              <td>
                <ul class="refs-list">
                  <li v-for="ref in item.sampleRefs" :key="`${item.legacyName}-${ref}`">
                    {{ ref }}
                  </li>
                </ul>
              </td>
            </tr>
            <tr v-if="!filteredIcons.length">
              <td colspan="5" class="empty-row">No icons match current filter.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import manifest from '../assets/icons-new/migration-manifest.json';

export default {
  name: 'IconMigration',
  data() {
    return {
      search: '',
      manifest,
    };
  },
  computed: {
    summary() {
      return this.manifest.summary || {
        usedTotal: 0,
        added: 0,
        missing: 0,
        progressPct: 0,
      };
    },
    sortedIcons() {
      const list = this.manifest.icons || [];
      return [...list].sort((a, b) => {
        if (a.newSvgExists !== b.newSvgExists) {
          return a.newSvgExists ? 1 : -1;
        }
        return a.legacyName.localeCompare(b.legacyName);
      });
    },
    filteredIcons() {
      if (!this.search) {
        return this.sortedIcons;
      }

      const query = this.search.toLowerCase();
      return this.sortedIcons.filter((icon) => (
        icon.legacyName.toLowerCase().includes(query)
        || icon.newName.toLowerCase().includes(query)
        || icon.newComponent.toLowerCase().includes(query)
      ));
    },
  },
};
</script>

<style scoped>
#icon-migration {
  min-height: 100vh;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.stat-card {
  background: #1b242e;
  border: 1px solid #2a3441;
  border-radius: 8px;
  padding: 12px;
}

.stat-label {
  font-size: 12px;
  color: #98a6c6;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
}

.search-input {
  background: #1b242e;
  border: 1px solid #2a3441;
  color: #cdd6ee;
  border-radius: 6px;
  padding: 8px 10px;
  min-width: 280px;
}

.table-wrap {
  overflow-x: auto;
  border: 1px solid #2a3441;
  border-radius: 8px;
}

.migration-table {
  width: 100%;
  border-collapse: collapse;
  background: #13191d;
}

.migration-table th,
.migration-table td {
  border-bottom: 1px solid #2a3441;
  text-align: left;
  vertical-align: top;
  padding: 10px;
  font-size: 13px;
}

.icon-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.preview-icon {
  font-size: 18px;
  width: 20px;
  text-align: center;
}

.name {
  color: #dce4fb;
}

.status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.status-added {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
}

.status-missing {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

.refs-list {
  list-style: none;
  padding: 0;
  margin: 0;
  color: #98a6c6;
  line-height: 1.35;
}

.empty-row {
  text-align: center;
  color: #98a6c6;
}

@media (max-width: 920px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .search-input {
    min-width: 100%;
  }
}
</style>
